import { io, ioEither, json, option } from "fp-ts";
import { toError } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { useEffect, useState } from "react";

const storage = {
  setItem: (key: string) => (value: string) => ioEither.tryCatch(() => sessionStorage.setItem(key, value), toError),
  getItem: (key: string) =>
    pipe(
      ioEither.tryCatch(() => sessionStorage.getItem(key), toError),
      ioEither.map(option.fromNullable)
    ),
};

export const useSessionStorage = <T>(key: string, initialValue: T): [T, (value: T) => void] => {
  const [state, setState] = useState<T>(() => {
    return pipe(
      key,
      storage.getItem,
      ioEither.chain(
        option.fold(
          () => ioEither.of(initialValue),
          (value) =>
            pipe(
              value,
              json.parse,
              ioEither.fromEither,
              ioEither.mapLeft(toError),
              ioEither.map((v) => (v as unknown) as T)
            )
        )
      ),
      ioEither.getOrElse(() => io.of(initialValue))
    )();
  });

  useEffect(() => {
    pipe(
      ioEither.of(storage.setItem),
      ioEither.ap(ioEither.of(key)),
      ioEither.ap(pipe(state, json.stringify, ioEither.fromEither)),
      ioEither.flatten
    )();
  }, [key, state]);

  return [state, setState];
};
